// bluaIQ — Compare Prices unit conversion + comparable-price engine.
//
// Pure, framework-free utility — the DETERMINISTIC half of the hybrid Compare
// Prices model. The code calculates and validates (unit normalization, MBF /
// package conversion, board-foot math, dimension/grade/species/treatment
// parsing, comparable price + savings, best-by-price/spec/value). The backend
// relationship classifier interprets/explains; the user confirms.
//
// Hard rules:
//   • Never mutates the source line; converted prices are returned separately
//     and are for comparison/recommendation only.
//   • Returns stable CODES (no UI strings) — the FE renders bilingual labels.
//   • A conversion is only performed when its inputs are actually present
//     (e.g. MBF→each needs thickness AND width AND length). Otherwise the line
//     is BLOCKED with a specific reason; it is never ranked as cheaper and group
//     savings are not calculated.

export type ConvUnit =
  | 'EA' | 'MBF' | 'BOX' | 'CASE' | 'BUNDLE' | 'PACK' | 'ROLL' | 'SHEET' | 'PANEL' | 'LF' | 'SF' | 'SQ' | 'OTHER'

export type ConversionStatus = 'direct' | 'converted' | 'blocked'

export type ConversionBlockCode =
  | 'MISSING_PRICE'
  | 'MISSING_LUMBER_DIMENSIONS'
  | 'MISSING_PACKAGE_QTY'
  | 'MISSING_ROLL_LENGTH'
  | 'MISSING_COVERAGE'
  | 'MISSING_PIECE_LENGTH'
  | 'MISSING_SHEET_DIMENSIONS'
  | 'UNIT_NOT_CONVERTIBLE'

export interface ConvAttr { key: string; value: string }
export interface ConvLineInput {
  id: string
  supplierName: string
  description: string
  unit?: string | null
  quantity?: number | null
  unitPrice?: number | null
  attributes?: ConvAttr[] | null
}

export interface SpecProfile {
  gradeCode: string | null
  gradeScore: number
  speciesCode: string | null
  speciesScore: number
  treated: boolean
  /** Composite score: grade dominates, then species, then treatment. */
  score: number
}

export interface ComparableLine {
  id: string
  supplierName: string
  description: string
  originalUnit: ConvUnit
  originalUnitLabel: string
  originalUnitPrice: number | null
  quantity: number | null
  comparableUnit: 'each' | null
  /** Price per each — converted; for comparison/recommendation ONLY. */
  comparableUnitPrice: number | null
  status: ConversionStatus
  blockedReasonCode?: ConversionBlockCode
  conversionBasis?: 'EACH' | 'MBF_LUMBER' | 'PACKAGE_QTY'
  spec: SpecProfile
}

export interface ComparisonResult {
  lines: ComparableLine[]
  comparableUnit: 'each' | null
  best: ComparableLine | null
  comparedAgainst: ComparableLine | null
  savingsPerEach: number | null
  allComparable: boolean
  anyConverted: boolean
  anyBlocked: boolean
  originalUnits: ConvUnit[]
  blockedReasonCode?: ConversionBlockCode
  // Executive guidance (deterministic) for 1..N comparable options.
  bestByPrice: ComparableLine | null
  bestBySpec: ComparableLine | null
  bestOverallValue: ComparableLine | null
  comparableCount: number
}

export interface LumberDims {
  thicknessIn: number
  widthIn: number
  lengthFt: number
  lengthFromInches: boolean
}

const EA_TOKENS = ['ea', 'each', 'pc', 'pcs', 'piece', 'pieces', 'unit', 'units', 'item', 'items', '']

export function normalizeUnit(unit?: string | null): ConvUnit {
  const u = (unit || '').toLowerCase().replace(/[^a-z]/g, '')
  if (EA_TOKENS.includes(u)) return 'EA'
  if (['mbf', 'mbft', 'mbdft'].includes(u)) return 'MBF'
  if (['box', 'bx', 'boxes'].includes(u)) return 'BOX'
  if (['case', 'cs', 'cases', 'ctn', 'carton'].includes(u)) return 'CASE'
  if (['bundle', 'bdl', 'bnd', 'bndl', 'bunch'].includes(u)) return 'BUNDLE'
  if (['pack', 'pk', 'pkg', 'package', 'packs'].includes(u)) return 'PACK'
  if (['roll', 'rl', 'rolls'].includes(u)) return 'ROLL'
  if (['sheet', 'sht', 'sheets'].includes(u)) return 'SHEET'
  if (['panel', 'pnl', 'panels'].includes(u)) return 'PANEL'
  if (['lf', 'lnft', 'linft', 'linealfoot', 'linealfeet', 'linearfoot', 'linearfeet', 'lft'].includes(u)) return 'LF'
  if (['sf', 'sqft', 'squarefoot', 'squarefeet', 'sqfeet'].includes(u)) return 'SF'
  if (['sq', 'square', 'squares'].includes(u)) return 'SQ'
  return 'OTHER'
}

export function unitDisplay(unit: ConvUnit): string {
  switch (unit) {
    case 'EA': return 'each'
    case 'MBF': return 'MBF'
    case 'BOX': return 'box'
    case 'CASE': return 'case'
    case 'BUNDLE': return 'bundle'
    case 'PACK': return 'pack'
    case 'ROLL': return 'roll'
    case 'SHEET': return 'sheet'
    case 'PANEL': return 'panel'
    case 'LF': return 'LF'
    case 'SF': return 'SF'
    case 'SQ': return 'SQ'
    default: return 'unit'
  }
}

/** Parse one dimension token: "2", "92.625", "92-5/8", "92 5/8", "15/32", "5/8". */
export function parseDimToken(raw: string): number | null {
  const s = raw.trim()
  let m = s.match(/^(\d+)\s*[-\s]\s*(\d+)\s*\/\s*(\d+)$/) // whole + fraction
  if (m) return parseInt(m[1], 10) + parseInt(m[2], 10) / parseInt(m[3], 10)
  m = s.match(/^(\d+)\s*\/\s*(\d+)$/) // pure fraction
  if (m) return parseInt(m[1], 10) / parseInt(m[2], 10)
  const f = parseFloat(s)
  return Number.isFinite(f) ? f : null
}

const DIM_TOKEN = String.raw`\d+(?:\.\d+)?(?:\s*[-\s]\s*\d+\s*\/\s*\d+)?|\d+\s*\/\s*\d+`
const DIM_RE = new RegExp(
  `(${DIM_TOKEN})\\s*(?:in\\.?|inch(?:es)?|")?\\s*[x×]\\s*` +
  `(${DIM_TOKEN})\\s*(?:in\\.?|inch(?:es)?|")?\\s*[x×]\\s*` +
  `(${DIM_TOKEN})\\s*(in\\.?|inch(?:es)?|"|ft\\.?|feet|foot|')?`,
  'i',
)

function resolveLengthFt(lengthVal: number, lengthRaw: string, annotation: string | undefined): { lengthFt: number; lengthFromInches: boolean } {
  const ann = (annotation || '').toLowerCase()
  if (/^(ft|feet|foot|')/.test(ann)) return { lengthFt: lengthVal, lengthFromInches: false }
  if (/^(in|inch|")/.test(ann)) return { lengthFt: lengthVal / 12, lengthFromInches: true }
  // No annotation: a fractional or ≥25 length reads as inches (e.g. 92-5/8").
  const hasFraction = /\//.test(lengthRaw)
  const fromInches = hasFraction || lengthVal >= 25
  return { lengthFt: fromInches ? lengthVal / 12 : lengthVal, lengthFromInches: fromInches }
}

/**
 * Parse "2x12x16", "2 in. x 4 in. x 16 ft", "2x4x92-5/8" → lumber dims. The
 * third dimension is FEET unless annotated/forced to inches.
 */
export function parseLumberDimensions(text?: string | null): LumberDims | null {
  if (!text) return null
  const m = text.match(DIM_RE)
  if (!m) return null
  const thicknessIn = parseDimToken(m[1])
  const widthIn = parseDimToken(m[2])
  const lengthVal = parseDimToken(m[3])
  if (thicknessIn == null || widthIn == null || lengthVal == null) return null
  if (thicknessIn <= 0 || widthIn <= 0 || lengthVal <= 0) return null
  const { lengthFt, lengthFromInches } = resolveLengthFt(lengthVal, m[3], m[4])
  return { thicknessIn, widthIn, lengthFt, lengthFromInches }
}

const ATTR_KEY = (attrs: ConvAttr[] | null | undefined, re: RegExp) =>
  (attrs || []).find((a) => re.test(a.key))?.value

/** Assemble lumber dims from extracted attributes when the description lacks them. */
export function parseLumberDimensionsFromAttributes(attrs?: ConvAttr[] | null): LumberDims | null {
  if (!attrs || !attrs.length) return null
  // A combined size/dimensions attribute (e.g. "2x12x10").
  const combined = ATTR_KEY(attrs, /^(size|dimensions?|nominal)$/i)
  if (combined) {
    const d = parseLumberDimensions(combined)
    if (d) return d
  }
  const tRaw = ATTR_KEY(attrs, /thick/i)
  const wRaw = ATTR_KEY(attrs, /width/i)
  const lRaw = ATTR_KEY(attrs, /length|^len$/i)
  if (tRaw == null || wRaw == null || lRaw == null) return null
  const thicknessIn = parseDimToken(String(tRaw))
  const widthIn = parseDimToken(String(wRaw))
  const lengthVal = parseDimToken(String(lRaw))
  if (thicknessIn == null || widthIn == null || lengthVal == null) return null
  if (thicknessIn <= 0 || widthIn <= 0 || lengthVal <= 0) return null
  const lAnn = String(lRaw).toLowerCase().match(/in\.?|inch(?:es)?|"|ft\.?|feet|foot|'/)?.[0]
  const { lengthFt, lengthFromInches } = resolveLengthFt(lengthVal, String(lRaw), lAnn)
  return { thicknessIn, widthIn, lengthFt, lengthFromInches }
}

export function boardFeetPerPiece(d: LumberDims): number {
  return (d.thicknessIn * d.widthIn * d.lengthFt) / 12
}
export function piecesPerMBF(d: LumberDims): number {
  const bf = boardFeetPerPiece(d)
  return bf > 0 ? 1000 / bf : 0
}

export function parsePackageQty(text?: string | null, attributes?: ConvAttr[] | null): number | null {
  const fromAttr = (attributes || []).find((a) => /pack|qty|count|per[-_ ]?(box|case|bundle|pkg)/i.test(a.key))
  if (fromAttr) {
    const n = parseInt(String(fromAttr.value).replace(/[^\d]/g, ''), 10)
    if (Number.isFinite(n) && n > 0) return n
  }
  const t = text || ''
  const patterns: RegExp[] = [
    /(\d+)\s*\/\s*(?:case|cs|box|bx|bundle|bdl|pkg|pack|carton|ctn)\b/i,
    /(?:box|case|bundle|carton|pack(?:age)?)\s*of\s*(\d+)/i,
    /(\d+)\s*(?:per|\/)\s*(?:case|box|bundle|pack|carton)\b/i,
    /(\d+)\s*(?:pk|pack|ct|count)\b/i,
    /\((\d+)\)\s*(?:pc|pcs|piece|count|ct)?/i,
  ]
  for (const re of patterns) {
    const m = t.match(re)
    if (m) {
      const n = parseInt(m[1], 10)
      if (Number.isFinite(n) && n > 0) return n
    }
  }
  return null
}

// ── deterministic grade / species / treatment detection ─────────────────────

export function detectGrade(text?: string | null): { code: string | null; score: number } {
  const t = (text || '').toLowerCase()
  if (/\bpremium\b/.test(t)) return { code: 'premium', score: 5 }
  if (/\bselect\b|\bsel\b|#\s*1\s*(?:&|and)\s*btr|no\.?\s*1\s*(?:&|and)\s*btr/.test(t)) return { code: 'select', score: 4 }
  if (/#\s*1\b|no\.?\s*1\b|grade\s*1\b/.test(t)) return { code: '#1', score: 3 }
  if (/#\s*2\b|no\.?\s*2\b|grade\s*2\b|\bstud\b/.test(t)) return { code: '#2', score: 2 }
  if (/#\s*3\b|no\.?\s*3\b|grade\s*3\b/.test(t)) return { code: '#3', score: 1 }
  return { code: null, score: 0 }
}

export function detectSpecies(text?: string | null): { code: string | null; score: number } {
  const t = (text || '').toLowerCase()
  if (/\bsyp\b|southern\s+yellow\s+pine|\byp\b/.test(t)) return { code: 'syp', score: 4 }
  if (/\bdoug(?:las)?\s*fir\b|\bdf\b|\bd-?fir\b/.test(t)) return { code: 'df', score: 3 }
  if (/\bhem[-\s]?fir\b|\bhf\b/.test(t)) return { code: 'hem-fir', score: 2 }
  if (/\bspf\b|spruce|pine[-\s]?fir/.test(t)) return { code: 'spf', score: 1 }
  return { code: null, score: 0 }
}

export function detectTreatment(text?: string | null): boolean {
  const t = (text || '').toLowerCase()
  return /\btreated\b|\bborate\b|ground\s*contact|pressure[-\s]?treated|\bacq\b|\bmca\b|\bpt\b/.test(t)
}

export function detectSpec(line: ConvLineInput): SpecProfile {
  const text = [line.description, ...(line.attributes || []).map((a) => `${a.key} ${a.value}`)].join(' ')
  const grade = detectGrade(text)
  const species = detectSpecies(text)
  const treated = detectTreatment(text)
  return {
    gradeCode: grade.code, gradeScore: grade.score,
    speciesCode: species.code, speciesScore: species.score,
    treated,
    score: grade.score * 10 + species.score + (treated ? 0.5 : 0),
  }
}

const NON_EA_BLOCK_REASON: Record<ConvUnit, ConversionBlockCode> = {
  EA: 'UNIT_NOT_CONVERTIBLE', MBF: 'MISSING_LUMBER_DIMENSIONS',
  BOX: 'MISSING_PACKAGE_QTY', CASE: 'MISSING_PACKAGE_QTY', BUNDLE: 'MISSING_PACKAGE_QTY', PACK: 'MISSING_PACKAGE_QTY',
  ROLL: 'MISSING_ROLL_LENGTH', SHEET: 'MISSING_SHEET_DIMENSIONS', PANEL: 'MISSING_SHEET_DIMENSIONS',
  LF: 'MISSING_PIECE_LENGTH', SF: 'MISSING_COVERAGE', SQ: 'MISSING_COVERAGE', OTHER: 'UNIT_NOT_CONVERTIBLE',
}

export function convertLineToEach(line: ConvLineInput): ComparableLine {
  const originalUnit = normalizeUnit(line.unit)
  const originalUnitLabel = (line.unit || '').trim() || unitDisplay(originalUnit)
  const price = typeof line.unitPrice === 'number' && Number.isFinite(line.unitPrice) ? line.unitPrice : null
  const quantity = typeof line.quantity === 'number' && Number.isFinite(line.quantity) ? line.quantity : null
  const spec = detectSpec(line)
  const base: ComparableLine = {
    id: line.id, supplierName: line.supplierName, description: line.description,
    originalUnit, originalUnitLabel, originalUnitPrice: price, quantity,
    comparableUnit: null, comparableUnitPrice: null, status: 'blocked', spec,
  }
  if (price == null) return { ...base, blockedReasonCode: 'MISSING_PRICE' }

  if (originalUnit === 'EA') {
    return { ...base, comparableUnit: 'each', comparableUnitPrice: price, status: 'direct', conversionBasis: 'EACH' }
  }
  if (originalUnit === 'MBF') {
    // Look in the description first, then in extracted attributes (QA #4: dims
    // may be parsed out as attributes, not left in the description string).
    const dims = parseLumberDimensions(line.description) || parseLumberDimensionsFromAttributes(line.attributes)
    if (dims) {
      const ppm = piecesPerMBF(dims)
      if (ppm > 0) return { ...base, comparableUnit: 'each', comparableUnitPrice: price / ppm, status: 'converted', conversionBasis: 'MBF_LUMBER' }
    }
    return { ...base, blockedReasonCode: 'MISSING_LUMBER_DIMENSIONS' }
  }
  if (originalUnit === 'BOX' || originalUnit === 'CASE' || originalUnit === 'BUNDLE' || originalUnit === 'PACK') {
    const qty = parsePackageQty(line.description, line.attributes)
    if (qty && qty > 0) return { ...base, comparableUnit: 'each', comparableUnitPrice: price / qty, status: 'converted', conversionBasis: 'PACKAGE_QTY' }
    return { ...base, blockedReasonCode: 'MISSING_PACKAGE_QTY' }
  }
  return { ...base, blockedReasonCode: NON_EA_BLOCK_REASON[originalUnit] }
}

const round2 = (n: number) => Math.round(n * 100) / 100

export function compareQuoteLines(lines: ConvLineInput[]): ComparisonResult {
  const all = lines.map(convertLineToEach)
  const priced = all.filter((l) => l.originalUnitPrice != null)
  const comparable = priced.filter((l) => l.comparableUnitPrice != null)
  const blocked = priced.filter((l) => l.comparableUnitPrice == null)
  const anyConverted = comparable.some((l) => l.status === 'converted')
  const anyBlocked = blocked.length > 0
  const allComparable = priced.length > 0 && !anyBlocked

  const byPrice = [...comparable].sort((a, b) => (a.comparableUnitPrice as number) - (b.comparableUnitPrice as number))
  const best = byPrice[0] ?? null
  const comparedAgainst = byPrice[1] ?? null
  const savingsPerEach = allComparable && best && comparedAgainst
    ? round2((comparedAgainst.comparableUnitPrice as number) - (best.comparableUnitPrice as number))
    : null

  // Deterministic executive guidance.
  const bestByPrice = best
  const maxSpec = comparable.reduce((mx, l) => Math.max(mx, l.spec.score), -Infinity)
  const bySpec = [...comparable].sort((a, b) => (b.spec.score - a.spec.score) || ((a.comparableUnitPrice as number) - (b.comparableUnitPrice as number)))
  const bestBySpec = bySpec[0] ?? null
  // Best value = cheapest among the highest-spec tier.
  const topTier = comparable.filter((l) => l.spec.score === maxSpec)
  const bestOverallValue = topTier.length
    ? [...topTier].sort((a, b) => (a.comparableUnitPrice as number) - (b.comparableUnitPrice as number))[0]
    : best

  return {
    lines: all,
    comparableUnit: comparable.length ? 'each' : null,
    best, comparedAgainst, savingsPerEach, allComparable, anyConverted, anyBlocked,
    originalUnits: Array.from(new Set(priced.map((l) => l.originalUnit))),
    blockedReasonCode: blocked[0]?.blockedReasonCode,
    bestByPrice, bestBySpec, bestOverallValue, comparableCount: comparable.length,
  }
}
